<template>
  <b-container>
    <b-form-group :disabled="changes_disabled">
      <communicating-tables-lists
        :brick_id="brick_id"
        :rhs_title="$t('used_tables')"
        :all_tables="tables_undeleted"
        :rhs_elements="entry_table_ids"
        :element_constructor="rhs_table_structure"
        @input="maj($event)"
      >
        <template v-slot:rhs_table_component="slot_props">
          <b-list-group-item class="table-handle hand-pointer">
            <span class="fas fa-grip-vertical fa-fw" />
            {{ slot_props.element_name }}
          </b-list-group-item>
        </template>
      </communicating-tables-lists>

      <b-form-group class="mt-3">
        <label>
          <b> {{ $t('table_chosen') }} </b>
        </label>
        <b-form-select
          v-model="reference_table_id"
          :options="choosable_table_ids"
        />
      </b-form-group>

      <b-card v-if="reference_table_id !== -1">
        <b-form-group>
          <label>
            <b> {{ $t('table_creation_mode') }} </b>
          </label>
          <b-form-radio-group v-model="creation_mode_with_table">
            <b-form-radio value="empty">
              {{ $t('new_table_creation_mode') }}
            </b-form-radio>
            <b-form-radio value="stacked">
              {{ $t('stacked_creation_mode') }}
            </b-form-radio>
          </b-form-radio-group>
        </b-form-group>
      </b-card>

      <b-card :header="$t('initial_columns_listing')" class="mt-3">
        <b-list-group>
          <lazy-list-display-y
            :nb="columns.length"
            class="synaplus-cosmo-h-480"
          >
            <template #default="{ nby, height, initialized }">
              <b-list-group-item
                v-if="initialized"
                :key="nby"
                :style="height"
                >{{ columns[nby] }}</b-list-group-item
              ><b-list-group-item v-else :key="nby">&nbsp;</b-list-group-item>
            </template>
          </lazy-list-display-y>
        </b-list-group>
      </b-card>

      <b-card :header="$t('new_columns_listing')" class="mt-3">
        <b-list-group>
          <draggable
            v-model="new_columns"
            class="drag-area"
            group="created_cols"
            handle=".dispatch-table-handle"
            @start="editing_nb = -1"
          >
            <div v-for="(element, index) in new_columns" :key="element[0].name">
              <b-list-group-item>
                <div
                  class="hand-pointer dispatch-table-handle d-flex flex-column flex-md-row justify-content-between align-items-center"
                >
                  <span class="fas fa-fw fa-grip-vertical" />
                  <span class="flex-grow-1 px-1">
                    <span v-if="editing_nb !== index">
                      {{ element[0].name }}
                    </span>
                    <manual-column-name
                      v-else
                      v-model="editing_generic_column_name"
                      :in_edition="editing_nb === index"
                    />
                  </span>

                  <b-button-group>
                    <b-button
                      size="sm"
                      variant="outline-secondary"
                      @click="toggle_editing(index)"
                    >
                      <span
                        v-if="editing_nb === index"
                        class="fas fa-check fa-xs"
                      />
                      <span v-else class="fas fa-pen fa-xs" />
                    </b-button>
                    <b-button
                      size="sm"
                      variant="outline-secondary"
                      @click="delete_col(index)"
                    >
                      <span class="fas fa-trash fa-xs" />
                    </b-button>
                  </b-button-group>
                </div>
                <hr class="chosen_table_separator" />
                <code-editor
                  v-if="editing_nb !== index"
                  :key="`editor-0-${index}`"
                  v-model="element[1]"
                  :columns="columns"
                  :disabled="true"
                />
                <code-editor
                  v-else
                  :key="`editor-1-${index}`"
                  v-model="editing_column_formula"
                  :columns="columns"
                  :disabled="changes_disabled"
                />
              </b-list-group-item>
            </div>
          </draggable>
        </b-list-group>
      </b-card>

      <b-card :header="$t('new_column')" class="mt-3">
        <b-form-group>
          <label :for="$id('title')" class="font-weight-bold">
            {{ $t('name') }}
          </label>
          <manual-column-name
            :id="$id('title')"
            v-model="new_generic_column_name"
            @input="editing_nb = -1"
          />

          <label :for="$id('formula')" class="mt-4 font-weight-bold">
            {{ $t('formula') }}
          </label>
          <code-editor
            v-model="new_column_formula"
            :columns="columns"
            :disabled="changes_disabled"
          />
          <b-button
            variant="outline-secondary"
            class="mt-4"
            @click="add_manual_feature_engineered_column"
          >
            {{ $t('add') }}
          </b-button>
        </b-form-group>
      </b-card>

      <unroll-element :title="$t('code_stdout')" :available="true">
        <unroll-element
          v-for="(metavariables_dict_poid, index) in metavariables_dicts_poids"
          :title="stringified_dict(metavariables_dict_poid)"
          :available="true"
        >
          <div
            v-for="element of metavariables_dicts_columns_stdout[index]"
            :key="index + JSON.stringify(element)"
          >
            <p>
              <!-- N.B PL : Do we display the metavar span too ?
              Or do we display the computed name, since this will be
              probably needed for the dictionary ? -->
              <b> {{ element[0] }} </b>
            </p>
            <pre>{{ element[1] }}</pre>
          </div>
        </unroll-element>
      </unroll-element>

      <b-card :header="$t('shared_option')" class="mt-3">
        <b-form-group>
          <label>
            <b> {{ $t('use_project_code') }} </b>
          </label>
          <b-form-checkbox
            v-model="use_project_code"
            :value="true"
            :unchecked-value="false"
          >
          </b-form-checkbox>
        </b-form-group>
      </b-card>
    </b-form-group>
  </b-container>
</template>

<i18n>
{
  "en": {
    "initial_columns_listing": "List of initially present columns",
    "new_columns_listing": "List of new created columns",
    "new_column": "New column",
    "manual_chosen_tables": "Reference chosen tables",
    "no_table": "None",
    "used_tables": "Used tables",
    "use_project_code": "Use project code",
    "code_stdout": "Standard output",
    "shared_option": "Shared option"
  },
  "fr": {
    "initial_columns_listing": "Liste des colonne initialement présentes",
    "new_columns_listing": "Liste des nouvelles colonnes crées",
    "new_column": "Nouvelle colonne",
    "manual_chosen_tables": "Tables de référence",
    "no_table": "Aucune",
    "used_tables": "Tables utilisées",
    "use_project_code": "Utiliser le code spécifique au projet",
    "code_stdout": "Sortie standard",
    "shared_option": "Option partagée"
  }
}
</i18n>

<script>
import _ from 'lodash'

import Vue from 'vue'
import draggable from 'vuedraggable'
import { BListGroupItem } from 'bootstrap-vue'
import CodeEditor from '@/components/code_editor.vue'

import { table_columns } from '@/api/subscriptions.js'

import { global_brick_mixin } from '@/components/mixin.js'
import LazyListDisplayY from '@/components/lazy_list_display_y.vue'
import UnrollElement from '@/components/unroll_element.vue'
import CommunicatingTablesLists from '@/components/communicating_tables_lists.vue'
import {
  BasicTableInfoEntity,
  GenericColumnName,
} from '@/components/object_model.js'
import ManualColumnName from '@/components/manual_column_name.vue'

// *****************************************************************************

export default {
  name: 'BrickManualFeatureEngineering',
  components: {
    draggable,
    LazyListDisplayY,
    CodeEditor,
    ManualColumnName,
    UnrollElement,
    CommunicatingTablesLists,
  },

  mixins: [global_brick_mixin],

  data() {
    return {
      entry_table_ids: [],
      reference_table_id: -1,
      new_generic_column_name: new GenericColumnName(),
      new_column_formula: '',
      editing_generic_column_name: new GenericColumnName(),
      editing_column_formula: '',
      multiple_tables: false,
      new_columns: [],
      editing_nb: -1,
      creation_mode_with_table: 'stacked',
      use_project_code: false,
      rhs_table_structure: BasicTableInfoEntity,
      columns: [],
    }
  },
  computed: {
    metavariables_dicts_columns_stdout() {
      let res = _.map(
        this.tracking_infos,
        elt => ((elt || {}).code_output || {}).columns_stdout || {}
      )
      return res
    },
    creation_mode() {
      let res = ''
      if (this.reference_table_id === -1) {
        res = 'independent'
      } else {
        res = this.creation_mode_with_table
      }
      return res
    },

    choosable_table_ids() {
      let self = this
      let res = _.map(this.entry_table_ids, function(elt) {
        return {
          value: elt.uid,
          text: self.tables_undeleted['tables'][elt.uid].name,
        }
      })

      res.push({ value: -1, text: this.$t('no_table') })

      return res
    },

    adapted_data() {
      let reference_table_name = this.get_table_name_from_uid(
        this.reference_table_id
      )
      let adapted = {
        uid: this.brick_id,
        parents_info: this.entry_table_ids,
        reference_table_name: reference_table_name,
        created_columns: this.new_columns,
        creation_mode: this.creation_mode,
        use_project_code: this.use_project_code,
      }

      _.assignIn(adapted, this.description_name)

      return adapted
    },
  },
  watch: {
    cur_brick: {
      immediate: true,
      handler(new_val) {
        this.new_columns = _.cloneDeep(new_val.created_columns)
        this.entry_table_ids = new_val.parents_info
        let reference_table_uid = this.get_table_uid_from_name(
          new_val.reference_table_name
        )
        this.reference_table_id = reference_table_uid
        if (this.reference_table_id !== -1) {
          this.creation_mode_with_table = new_val.creation_mode
        }
        this.use_project_code = new_val.use_project_code
      },
    },

    choosable_table_ids: {
      immediate: true,
      handler(nv, ov) {
        if (!_.isEqual(nv, ov)) {
          this.update_colums_list()
        }
      },
    },

    reference_table_id(nv, ov) {
      if (!_.isEqual(nv, ov)) {
        this.update_colums_list()
      }
    },
  },
  methods: {
    // N.B PL : Maybe factorize this between
    // reference_table_id / junction_id / table_lhs
    get_table_name_from_uid(uid) {
      let res = ''
      // N.B PL : What to do if uid is not found ?
      if (uid !== -1) {
        res = this.bricks_undeleted['bricks'][uid].name
      }
      return res
    },
    // N.B PL : Maybe factorize this between
    // reference_table_id / junction_id / table_lhs
    get_table_uid_from_name(name) {
      /*
      Here name is the name of a parent table
      */
      let res = -1
      // N.B PL : What to do if name is not found among parent tables
      if (name !== '') {
        for (let info_entity of this.cur_brick.parents_info) {
          let uid = info_entity.uid
          let brick = this.bricks_undeleted['bricks'][uid]
          if (name === brick.name) {
            res = uid
            break
          }
        }
      }
      return res
    },

    add_manual_feature_engineered_column() {
      if (
        this.new_generic_column_name.name !== '' &&
        this.new_column_formula !== ''
      ) {
        this.new_columns.push([
          this.new_generic_column_name,
          this.new_column_formula,
        ])
        this.new_generic_column_name = new GenericColumnName()
        this.new_column_formula = ''
      }

      this.editing_nb = -1
    },

    toggle_editing(index) {
      if (this.editing_nb === -1) {
        this.editing_nb = index
        this.editing_generic_column_name = this.new_columns[index][0]
        this.editing_column_formula = this.new_columns[index][1]
      } else {
        this.editing_nb = -1
        Vue.set(this.new_columns, index, [
          this.editing_generic_column_name,
          this.editing_column_formula,
        ])
        this.editing_generic_column_name = new GenericColumnName()
        this.editing_column_formula = ''
      }
    },

    delete_col(index) {
      this.new_columns.splice(index, 1)
    },

    maj(event) {
      this.entry_table_ids = _.cloneDeep(event)
    },

    async update_colums_list() {
      let res1 = []
      let res2 = []
      let self = this
      let promises = _.map(this.entry_table_ids, async function(table) {
        let new_cols =
          (await table_columns(self.project_id, table.uid)).pipe_object || []
        if (table.uid === self.reference_table_id) {
          res1.push(...new_cols)
        } else {
          let complete_names = _.map(
            new_cols,
            elt => self.tables_undeleted['tables'][table.uid].name + '.' + elt
          )
          res2.push(...complete_names)
        }
      })
      await Promise.all(promises)

      const res = [...res1, ...res2]
      this.columns = res
    },
  },
}
</script>
